import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UiModule } from 'src/app/ui/ui.module';
import { DirectivesModule } from 'src/app/directives/directives.module';
import {
  DataSourceService,
  DataSourceConfigOptions,
  PaginationStrategy,
} from 'src/app/services/data-source.service';
import { Observable } from 'rxjs';
import { SelectionModalComponent, SelectionModalSections } from '../selection-modal.component';
import { FilterData } from '../../../generic-filters-view/filters.interface';

interface ComputedColumn<T> {
  key: string;
  flex: string;
  header: TemplateRef<void>;
  cell: TemplateRef<{ $implicit: T }>;
}

interface SelectionListModalColumn<T> {
  size?: number | 'fill' | 'fit';
  header: TemplateRef<void>;
  cell: TemplateRef<{ $implicit: T }>;
}

export type SelectionListModalSections<T> = Omit<SelectionModalSections, 'content'> & {
  columns: { [key: string]: SelectionListModalColumn<T> };
};

/**
 * A generic **list** selection dialog component for selecting entities with search capabilities.
 * Supports customizable content sections with the input `sections`.
 *
 * Not recommended to be used directly, this component is meant to be used as a generic
 * base for all possible types of list selection dialogs, namely `single-selection-list-modal` and
 * `multi-selection-list-modal`. Therefore, if wanting to use something complete out of the box,
 * please check `single-selection-list-modal` or `multi-selection-list-modal`.
 *
 * @template T The type of entity being displayed/selected in the dialog
 */
@UntilDestroy()
@Component({
  selector: 'app-selection-list-modal',
  templateUrl: './selection-list-modal.component.html',
  styleUrls: ['./selection-list-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    UiModule,
    DirectivesModule,
    SelectionModalComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionListModalComponent<T> implements OnInit, AfterViewInit {
  /**
   * Unique identifier for the dialog. Also used as a prefix for all
   * eligible elements for the `data-name` attribute.
   *
   * As an example, if the `id` value passed was `select-space-dialog`, the
   * entity select button's `data-name` attribute will be `select-space-dialog-select-item`.
   *
   * @required
   */
  @Input() id!: string;

  /**
   * The title displayed at the top of the dialog.
   *
   * @required
   */
  @Input() title!: string;

  /**
   * Placeholder text shown in the search input field.
   *
   * @required
   */
  @Input() searchPlaceholder!: string;

  /**
   * Configuration object that defines the customizable sections of the generic
   * entity selection dialog by using ng-template elements.
   * Currently, the supported configurable sections are `columns`, `contentHeader` and `footer`.
   *
   * `columns` is **required**. This is where you define the columns in a
   * `{ [key]: Column }` manner to be displayed in the entity list. The possible
   * properties for the `Column` object are the following:
   * - `size`: number | 'fill' | 'fit' - The size of the column, either as a percentage of the total width (between 0 and 1),
   * or as `fill` to fill the remaining space, or as `fit` to only take the necessary space to fit the content.
   * Defaults to `fill` if not provided.
   * - `header`: TemplateRef<void> - The `ng-template` to display as the header of the column.
   * - `cell`: TemplateRef<{ $implicit: T }> - The `ng-template` to display as the cell of the column,
   * for each item of the entity list. Has access to the current item of the entity list
   * by using `let-item` or similar as context variable.
   *
   * `contentHeader` is **optional**. It's the header of the content section and serves to display
   * an important information above the entity list, which could be a title for the list,
   * a message regarding the entries being displayed, the current event's time, etc.
   * If not provided, nothing will be displayed above the entity list.
   *
   * `disclaimer` is **optional**. It's the disclaimer of the dialog, which can be used
   * to display a disclaimer above the entity list, e.g. to display a disclaimer
   * regarding the entries being displayed.
   * If not provided, no disclaimer will be displayed.
   *
   * `footer` is **optional**. It's the footer of the dialog, which can be used
   * to customize the dialog's bottom section actions, e.g. to display a button
   * to close the dialog or to create a new entity.
   * If not provided, no footer will be displayed.
   *
   * @required
   *
   * @example
   * ```html
   * <app-selection-list-modal
   *   ...
   *   [sections]="{
   *     contentHeader,
   *     footer,
   *     columns: {
   *       actions: {
   *         header: actionsHeader,
   *         cell: actionsCell,
   *       }
   *     },
   *   }"
   * >
   *   <ng-template #contentHeader>
   *     <h3>Select an entity</h3>
   *   </ng-template>
   *
   *   <ng-template #actionsHeader></ng-template>
   *   <ng-template #actionsCell let-item>
   *     <button mat-icon-button (click)="selectItem(item)">
   *       <mat-icon>check</mat-icon>
   *     </button>
   *   </ng-template>
   *
   *   <ng-template #footer>
   *     <button mat-raised-button color="primary" (click)="closeDialog()">Close</button>
   *   </ng-template>
   * </app-selection-list-modal>
   * ```
   */
  @Input() sections!: SelectionListModalSections<T>;

  /**
   * Defines configuration for how the data will be fetched and handled.
   *
   * @interface DataSourceConfigOptions<T>
   *
   * @property {FetchStrategy} fetchStrategy - The strategy to use for fetching the data, which
   * can be only `server-side` for now.
   * @property {Function} fetch - The function to use for fetching the data. Receives a `FetchDataParams` object
   * as a parameter, which contains the current @see{@link IFilterAPI} parameter, page index and page size;
   * and should return an Observable that emits a `FetchDataResult<T>` object, which contains the items and the total items.
   * This function will mainly be called when the dialog is opened (`ngAfterViewInit`) and whenever the user updates
   * the search query (by clicking on the search button or pressing enter in the search input field).
   * @property {PaginationStrategy} paginationStrategy - The strategy to use for pagination
   * (e.g. `scroll-based` or `page-based`).
   * @property {number} pageSize - The number of items to display per page.
   *
   * @required
   *
   * @example
   * At the component class, you can define the `fetch` function as follows:
   *
   * ```typescript
   * FetchStrategy = FetchStrategy;
   * PaginationStrategy = PaginationStrategy;
   *
   * constructor(...) {
   *   this.fetch = this.fetch.bind(this);
   * }
   *
   * fetch(params: DataSourceFetchParams): Observable<DataSourceFetchResult<T>> {
   *   return this.apiService.getEntities(params).pipe(
   *     map((res) => ({
   *       items: res.items,
   *       totalItems: res.totalItems,
   *     })),
   *   );
   * }
   * ```
   *
   * And at the template, you can pass the `fetch` function as a parameter:
   *
   * ```html
   * <app-selection-list-modal
   *   ...
   *   [fetchConfig]="{
   *     fetchStrategy: FetchStrategy.ServerSide,
   *     fetch: fetch,
   *     paginationStrategy: PaginationStrategy.ScrollBased,
   *     pageSize: 10
   *   }"
   * >
   * ```
   */
  @Input() fetchConfig!: DataSourceConfigOptions<T>;

  /**
   * Observable that emits the filters to be displayed.
   *
   * Can be easily provided by using `FiltersFactoryService`, e.g.
   * `FiltersFactoryService.createProfilesFilters()`.
   */
  @Input() filters?: Observable<FilterData[]>;

  /**
   * Message to display when no entities are found.
   *
   * If not provided, the default message will be "No data found".
   */
  @Input() emptyMessage?: string;

  /**
   * Event emitter that triggers when the dialog is cancelled.
   *
   * If not subscribed to, the default behavior is that the dialog will close
   * normally when clicked at the close button, with no data being returned.
   */
  @Output() cancel = new EventEmitter<T[]>();

  @ViewChild(SelectionModalComponent) selectionModal!: SelectionModalComponent<T>;

  PaginationStrategy = PaginationStrategy;

  isLoading$ = this.dataSourceService.isLoading$;
  totalItems$ = this.dataSourceService.totalItems$;
  pageIndex$ = this.dataSourceService.pageIndex$;

  dataSource = new MatTableDataSource<T>();
  columnNames: string[] = [];
  shouldShowContentHeader = false;

  // Pre-computed columns data to avoid using functions in the template.
  computedColumns: ComputedColumn<T>[] = [];

  constructor(
    private dialogRef: MatDialogRef<SelectionListModalComponent<T>>,
    private dataSourceService: DataSourceService<T>,
  ) {
    this.showScrollBasedLoading = this.showScrollBasedLoading.bind(this);

    this.dataSourceService.data$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.dataSource.data = data;
    });
  }

  ngOnInit(): void {
    this.columnNames = [...Object.keys(this.sections.columns)];
    this.computedColumns = Object.entries(this.sections.columns).map(([key, value]) => ({
      key,
      flex: this.sizeToFlex(value.size),
      header: value.header,
      cell: value.cell,
    }));
  }

  ngAfterViewInit(): void {
    // This is needed to ensure that the fetchConfig is updated with the latest
    // config from the DataSourceService (which includes default values).
    this.fetchConfig = this.dataSourceService.config;

    this.shouldShowContentHeader =
      !!this.sections.contentHeader ||
      this.fetchConfig.paginationStrategy === PaginationStrategy.PageBased;
  }

  showScrollBasedLoading(itemIndex: number): boolean {
    return (
      this.fetchConfig.paginationStrategy === PaginationStrategy.ScrollBased &&
      itemIndex === this.dataSource.data.length - 1 &&
      !this.dataSourceService.isLastPage
    );
  }

  fetchNextItems(): void {
    if (
      this.fetchConfig.paginationStrategy !== PaginationStrategy.ScrollBased ||
      this.dataSourceService.isLoading ||
      this.dataSourceService.isEmpty ||
      this.dataSourceService.isLastPage
    ) {
      return;
    }

    this.dataSourceService.nextPageIndex();
    this.selectionModal.executeFetch(false, false);
  }

  onPageChange(event: PageEvent): void {
    this.dataSourceService.setPageIndex(event.pageIndex);
    // This empty data is needed to show the loading indicator.
    this.dataSource.data = [];
    this.selectionModal.executeFetch(false);
  }

  onCancel(): void {
    this.cancel.observed ? this.cancel.emit() : this.dialogRef.close();
  }

  private sizeToFlex(size: SelectionListModalColumn<T>['size']): string {
    if (!size) {
      size = 'fill';
    }

    switch (size) {
      case 'fill':
        return 'auto';
      case 'fit':
        return 'none';
      default:
        // The "{size}% - var(--selection-list-modal-table-row-gap)" is to account for the
        // gap between the columns, otherwise the next column will start a bit after it's supposed to.
        return `0 0 calc(${size * 100}% - var(--selection-list-modal-table-row-gap, 16px))`;
    }
  }
}
